import React, { FC, Fragment, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useDataProvider } from 'react-admin';
import { DateTime } from 'luxon';
import {
  GeographyPointDto,
  JobGeolocationHistoryDto,
  UserDto,
} from '@vatos-pas/common';
import {
  formatLatitudeDMS,
  formatLongitudeDMS,
} from 'components/LocationDetail/utils';

export const VIEW_ADDRESS_HISTORY_TOOLTIP = 'View address history';

export type AddressHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  jobId: string;
};

type FetchStatus = 'idle' | 'loading' | 'error' | 'data';

const formatUserName = (user: UserDto | undefined): string => {
  if (!user) return 'Unknown user';
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name.length > 0 ? name : 'Unknown user';
};

const formatHeaderTimestamp = (createdAt: Date | string): string => {
  const dt =
    typeof createdAt === 'string'
      ? DateTime.fromISO(createdAt)
      : DateTime.fromJSDate(createdAt);
  if (!dt.isValid) return '';
  return `${dt.toFormat('h:mm a')} - ${dt.toFormat('MM/dd/yyyy')}`;
};

const formatCityStateZip = (
  city: string | null,
  state: string | null,
  zip: string | null,
): string => {
  const stateZip = [state, zip].filter((part): part is string => !!part).join(' ');
  if (city && stateZip) return `${city}, ${stateZip}`;
  if (city) return city;
  return stateZip;
};

const formatGeoLine = (geo: GeographyPointDto | null): string => {
  if (!geo || !Array.isArray(geo.coordinates) || geo.coordinates.length < 2) {
    return 'Geolocation not recorded';
  }
  const [longitude, latitude] = geo.coordinates;
  const latDMS = formatLatitudeDMS(latitude);
  const lngDMS = formatLongitudeDMS(longitude);
  if (!latDMS || !lngDMS) return 'Geolocation not recorded';
  return `${latDMS} ${lngDMS}`;
};

export const AddressHistoryModal: FC<AddressHistoryModalProps> = ({
  open,
  onClose,
  jobId,
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();

  const [status, setStatus] = useState<FetchStatus>('idle');
  const [history, setHistory] = useState<JobGeolocationHistoryDto[]>([]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setStatus('loading');
    setHistory([]);

    dataProvider
      .getList<JobGeolocationHistoryDto>('job-geolocation-history', {
        filter: { jobId },
        sort: { field: 'createdAt', order: 'DESC' },
        pagination: { page: 1, perPage: 100 },
      })
      .then(response => {
        if (cancelled) return;
        setHistory(response.data ?? []);
        setStatus('data');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [open, jobId, dataProvider]);

  const renderBody = () => {
    if (status === 'loading' || status === 'idle') {
      return (
        <Box className={classes.stateMessage}>
          <CircularProgress size="1rem" />
        </Box>
      );
    }

    if (status === 'error') {
      return (
        <Box className={classes.stateMessage}>
          <Typography>Could not load address history.</Typography>
        </Box>
      );
    }

    if (history.length === 0) {
      return (
        <Box className={classes.stateMessage}>
          <Typography>No address history yet.</Typography>
        </Box>
      );
    }

    return history.map((row, index) => {
      const hasAnyAddressField =
        !!row.address1 ||
        !!row.address2 ||
        !!row.city ||
        !!row.state ||
        !!row.zip;
      const cityStateZip = formatCityStateZip(row.city, row.state, row.zip);

      return (
        <Fragment key={row.id}>
          <Box className={classes.row}>
            <Typography className={classes.header}>
              {`${formatUserName(row.updatedByUser)} - ${formatHeaderTimestamp(
                row.createdAt,
              )}`}
            </Typography>
            {!hasAnyAddressField && (
              <Typography className={classes.textItem}>
                Address not recorded
              </Typography>
            )}
            {hasAnyAddressField && row.address1 && (
              <Typography className={classes.textItem}>
                {row.address1}
              </Typography>
            )}
            {hasAnyAddressField && row.address2 && (
              <Typography className={classes.textItem}>
                {row.address2}
              </Typography>
            )}
            {hasAnyAddressField && cityStateZip && (
              <Typography className={classes.textItem}>
                {cityStateZip}
              </Typography>
            )}
            {(row.geo || hasAnyAddressField) && (
              <Typography className={classes.textItem}>
                {formatGeoLine(row.geo)}
              </Typography>
            )}
          </Box>
          {index !== history.length - 1 && (
            <Box className={classes.separator} />
          )}
        </Fragment>
      );
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="address-history-dialog-title"
    >
      <DialogTitle id="address-history-dialog-title">
        Address History
      </DialogTitle>
      <DialogContent className={classes.content} dividers>
        {renderBody()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  content: {
    maxHeight: '60vh',
  },
  stateMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px 0',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 0',
  },
  header: {
    fontWeight: 600,
    fontSize: '14px',
  },
  textItem: {
    fontSize: '14px',
  },
  separator: {
    height: 1,
    backgroundColor: theme.palette.divider,
    marginTop: 4,
    marginBottom: 4,
  },
}));

export default AddressHistoryModal;
